<template>
  <div>
    <div class="header-section">
      <div class="content-section">
        <h3 class="section-title">
          {{ $t("notifyMeSettings") }}
        </h3>
        
        <!-- Tab Navigation -->
        <div v-if="settings.settings.notifyme.is_active" class="border-b border-gray-200 mb-6">
          <ul class="flex flex-wrap -mb-px">
            <li class="mr-2">
              <a @click.prevent="activeTab = 'settings'" 
                 :class="{'border-main text-main border-b-2': activeTab === 'settings',
                         'border-transparent text-gray-500 hover:text-gray-600 hover:border-gray-300': activeTab !== 'settings'}"
                 class="inline-block p-4 border-b-2 cursor-pointer">
                {{ $t("settings") || "Settings" }}
              </a>
            </li>
            <li class="mr-2">
              <a @click.prevent="activeTab = 'subscribers'" 
                 :class="{'border-main text-main border-b-2': activeTab === 'subscribers',
                         'border-transparent text-gray-500 hover:text-gray-600 hover:border-gray-300': activeTab !== 'subscribers'}"
                 class="inline-block p-4 border-b-2 cursor-pointer">
                {{ $t("subscribersList") || "Subscribers" }}
              </a>
            </li>
          </ul>
        </div>
        
        <!-- Enable/Disable Notify Me feature -->
        <div v-if="activeTab === 'settings'" class="content mb-5 flex gap-4">
          <label
            class="switch peer"
            for="enable_notifyme"
          >
            <input
              id="enable_notifyme"
              v-model="settings.settings.notifyme.is_active"
              type="checkbox"
            />
            <div class="slider round"></div>
          </label>
          <div class="flex flex-col gap-1">
            <div class="text font-extralight">
              {{ $t("enableNotifyMe") }}
            </div>
            <div
              class="sub-text text-xs font-extralight text-[#696767]"
            >
              {{ $t("enableNotifyMeDescription") }}
            </div>
          </div>
        </div>

        <!-- Settings Tab Content -->
        <div v-if="settings.settings.notifyme.is_active && activeTab === 'settings'">
          <!-- Notification Settings -->
          <div class="content mb-5">
            <h4 class="text-lg font-medium mb-4">{{ $t("notificationSettings")  }}</h4>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div class="flex flex-col">
                <label class="text-sm mb-1">{{ $t('templateId')}}</label>
                <input
                  v-model="settings.settings.notifyme.template_id"
                  type="number"
                  class="input-main"
                  dir="ltr"
                />
              </div>
            </div>
            
            <div class="mb-5">
              <label class="text-sm mb-1">{{ $t('notificationText') }}</label>
              <textarea
                v-model="settings.settings.notifyme.notification_text"
                class="input-main h-24 w-full"
                :placeholder="$t('enterNotificationText') "
              ></textarea>
              <div class="text-xs text-gray-500 mt-1">
                {{ $t('availableVariables')}}: 
                <button @click="insertVariable('#productTitle#')" class="px-2 py-0.5 bg-gray-100 rounded-md text-xs">
                 {{$t('productTitle')}} (#productTitle#)
                </button>
                <button @click="insertVariable('#productUrl#')" class="px-2 py-0.5 bg-gray-100 rounded-md text-xs">
                  {{$t('productUrl')}}(#productUrl#)
                </button>
                <button @click="insertVariable('#Price#')" class="px-2 py-0.5 bg-gray-100 rounded-md text-xs">
                  {{$t('price')}} (#Price#)
                </button>
                <button @click="insertVariable('#Variation#')" class="px-2 py-0.5 bg-gray-100 rounded-md text-xs">
                  {{$t('variation')}} (#Variation#)
                </button>
              </div>
            </div>
          </div>
          
          <!-- Display Settings -->
          <div class="content mb-5">
            <h4 class="text-lg font-medium mb-4">{{ $t("displaySettings") }}</h4>

            <div class="mb-5">
              <label class="text-sm mb-1">{{ $t('buttonText') || "Button Text" }}</label>
              <input
                v-model="settings.settings.notifyme.button_text"
                type="text"
                class="input-main"
                :placeholder="$t('enterButtonText')"
              />
            </div>
      
          </div>
        </div>
        
        <!-- Subscribers Tab Content -->
        <div v-if="activeTab === 'subscribers'" class="content">
          <h4 class="text-lg font-medium mb-4">{{ $t("subscribersList") }}</h4>
          
          <!-- Search and Filter -->
          <div class="mb-4 flex items-center justify-between">
            <!-- Filter Button -->
            <div class="relative filter-dropdown-container">
              <button
                @click.stop="toggleFilterDropdown($event)"
                class="flex items-center gap-2 px-6 py-3 bg-[#F2F2F2] rounded-full hover:bg-gray-200"
              >
                <i class="icon-smsir-filter text-[20px]"></i>
                <span class="text-[#0D0D0D] text-base">{{ $t("filter") }}</span>
              </button>

              <!-- Filter Dropdown -->
              <div
                v-if="showFilterDropdown"
                class="absolute top-full right-0 mt-1 bg-white shadow-lg rounded-3xl p-6 z-10 min-w-[384px]"
                @click.stop
              >
                <!-- Status Filter -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    {{ $t("showedByStatus")  }}
                  </label>
                  <div class="space-y-2">
                    <label v-for="option in statusOptions" :key="option.value" class="flex items-center gap-2">
                      <input
                        type="checkbox"
                        :value="option.value"
                        :checked="selectedFilters.includes(option.value)"
                        @change="toggleFilter(option.value)"
                        class="mr-2"
                      />
                      <span>
                        {{ option.label === "all" ? ($t("common.all") ) : ($t(`${option.label}`) || option.label) }}
                      </span>
                    </label>
                  </div>
                </div>
                <hr class="color-[#E6E6E6] h-[1px] w-full mb-4">
                <!-- Items Per Page -->
                <div class="mb-5">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    {{ $t("itemsPerPage")  }}
                  </label>
                  <div class="space-y-2">
                    <label v-for="option in [10, 20, 50]" :key="option" class="flex items-center gap-2">
                      <input
                        type="radio"
                        :value="option"
                        v-model="perPage"
                        class="mr-2"
                      />
                      <span>{{ option }}</span>
                    </label>
                  </div>
                </div>

                <!-- Filter Buttons -->
                <div class="flex justify-between gap-20">
                  <button
                    @click="applyFilters"
                    class="py-3 px-6 bg-[#FFBF00] text-[#0D0D0D] text-base flex-1 rounded-full"
                  >
                    {{ $t("applyFilters") }}
                  </button>
                  <button
                    @click="resetFilters"
                    class="py-2 px-4 bg-white border rounded-full border-[#BE1A0E] text-[#BE1A0E] text-base flex-1"
                  >
                    {{ $t("resetFilters") }}
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Search Input -->
            <div class="relative" style="width: 340px">
              <input
                v-model="searchQuery"
                type="text"
                class="input-main w-full pl-10"
                :placeholder="$t('searchPlaceholder')"
                @input="debouncedSearch"
              />
              <i class="icon-smsir-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            </div>
          </div>
          
          <!-- Loading indicator -->
          <div v-if="loading" class="flex justify-center my-8">
            <LoadingSpinner />
          </div>
          
          <!-- Subscribers table -->
          <div v-else-if="subscribers.length > 0" class="overflow-x-auto">
            <table class="w-full border-collapse">
              <thead>
                <tr class="bg-gray-100">
                  <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('phone')  }}</th>
                  <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('product')  }}</th>
                  <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('status')  }}</th>
                  <th class="p-3 text-right text-[#4D4D4D] text-sm font-light">{{ $t('date')  }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(subscriber, index) in subscribers" :key="index" class="border-b hover:bg-gray-50">
                  <td class="p-3 text-base text-[#0D0D0D]">{{ subscriber.phone }}</td>
                  <td class="p-3 text-base text-[#0D0D0D]">{{ decodeProductTitle(subscriber.product_title) }}</td>
                  <td class="p-3">
                    <span :class="[
                      'px-3 py-2 rounded-full text-xs',
                      subscriber.status === 'sent' ? 'bg-[#E5FFF0] text-[#0E8B51] border border-[#0E8B51]' :
                      subscriber.status === 'pending' ? 'bg-[#FFF9E5] text-[#CC9900] border border-[#CC9900]' :
                      'bg-[#FDE9E7] text-[#D51D10] border border-[#D51D10]'
                    ]">
                      {{ getSmsStatusText(subscriber.status) }}
                    </span>
                  </td>
                  <td class="p-3 text-base text-[#0D0D0D]">{{ formatDate(subscriber.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- Empty state -->
          <div v-else class="text-center py-8">
            <p class="text-gray-500">{{ $t('noSubscribersFound') }}</p>
          </div>
          
          <!-- Pagination -->
          <div
            v-if="totalPages > 1"
            class="pagination mt-4"
          >
            <button
              @click="goToPage(currentPage - 1)"
              :disabled="currentPage === 1"
              class="px-3 py-1 border rounded"
              :class="{
                'opacity-50 cursor-not-allowed':
                  currentPage === 1,
              }"
            >
              {{ $t('previous')  }}
            </button>
            <span class="px-3 py-1">
              {{ currentPage }} / {{ totalPages }}
            </span>
            <button
              @click="goToPage(currentPage + 1)"
              :disabled="currentPage >= totalPages"
              class="px-3 py-1 border rounded"
              :class="{
                'opacity-50 cursor-not-allowed':
                  currentPage >= totalPages,
              }"
            >
              {{ $t('next')  }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import LoadingSpinner from "../LoadingSpinner.vue";

export default {
  components: {
    LoadingSpinner
  },
  props: {
    settings: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      activeTab: 'settings',
      subscribers: [],
      loading: false,
      searchQuery: '',
      statusFilter: '',
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      perPage: 10,
      showFilterDropdown: false,
      selectedFilters: ["all"],
      statusOptions: [
        { value: 'all', label: 'all' },
        { value: 'pending', label: 'pendingToSend' },
        { value: 'sent', label: 'sent' }
      ],
      debounceTimer: null
    };
  },
  computed: {
    paginationPages() {
      const pages = [];
      const totalPagesToShow = 5;
      
      if (this.totalPages <= totalPagesToShow) {
        // Show all pages if total pages is less than what we want to show
        for (let i = 1; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Always show the first page
        pages.push(1);
        
        // Current page is close to the beginning
        if (this.currentPage <= 3) {
          pages.push(2, 3, '...', this.totalPages);
        } 
        // Current page is close to the end
        else if (this.currentPage >= this.totalPages - 2) {
          pages.push('...', this.totalPages - 2, this.totalPages - 1, this.totalPages);
        } 
        // Current page is in the middle
        else {
          pages.push('...', this.currentPage - 1, this.currentPage, this.currentPage + 1, '...', this.totalPages);
        }
      }
      
      return pages;
    }
  },
  watch: {
    activeTab(newVal) {
      if (newVal === 'subscribers') {
        this.loadSubscribers();
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    insertVariable(variable) {
      if (!this.settings.settings.notifyme.notification_text) {
        this.settings.settings.notifyme.notification_text = '';
      }
      this.settings.settings.notifyme.notification_text += ' ' + variable;
    },
    loadSubscribers() {
      this.loading = true;
      
      const data = {
        action: 'smsir_get_subscribers',
        nonce: window.sms_ir_login.nonce,
        page: this.currentPage,
        per_page: this.perPage,
        search: this.searchQuery,
        status: this.statusFilter
      };
      
      axios.post(window.sms_ir_login.ajaxUrl, new URLSearchParams(data))
        .then(response => {
          if (response.data.success) {
            this.subscribers = response.data.data.subscribers;
            this.totalPages = response.data.data.pages;
            this.totalItems = response.data.data.total;
            this.currentPage = response.data.data.current_page;
          } else {
            this.subscribers = [];
            console.error('Error loading subscribers:', response.data.message);
          }
        })
        .catch(error => {
          console.error('Error loading subscribers:', error);
          this.subscribers = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.loadSubscribers();
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
      }).format(date);
    },
    getSmsStatusText(status) {
      switch (status) {
        case 'sent':
          return this.$t('sent') || 'Sent';
        case 'pending':
          return this.$t('pendingToSend') || 'Pending';
        case 'failed':
          return this.$t('failed') || 'Failed';
        default:
          return status;
      }
    },
    formatVariationAttributes(attributes) {
      if (!attributes) return '';
      
      return Object.entries(attributes)
        .map(([key, value]) => {
          // Remove 'attribute_' prefix if it exists
          const cleanKey = key.replace('attribute_', '');
          // Convert pa_ prefix to empty string
          const displayKey = cleanKey.replace('pa_', '');
          return `${displayKey}: ${value}`;
        })
        .join(', ');
    },
    decodeProductTitle(title) {
      if (!title) return '';
      
      try {
        // First replace + with space
        let decoded = title.replace(/\+/g, ' ');
        
        // Try decoding multiple times if needed
        try {
          while (decoded.includes('%')) {
            const prevDecoded = decoded;
            decoded = decodeURIComponent(decoded);
            if (prevDecoded === decoded) break;
          }
        } catch (e) {
          // If decoding fails, return the partially decoded string
          console.warn('Partial decode error:', e);
        }
        
        // Clean up any remaining encoding artifacts
        decoded = decoded.replace(/%3A/g, ':')
                        .replace(/%2C/g, ',')
                        .replace(/%20/g, ' ');
        
        return decoded;
      } catch (e) {
        console.error('Error decoding product title:', e);
        return title;
      }
    },
    toggleFilterDropdown(event) {
      event.stopPropagation();
      this.showFilterDropdown = !this.showFilterDropdown;
    },
    toggleFilter(value) {
      if (value === "all") {
        // If 'all' is selected, clear other filters
        this.selectedFilters = ["all"];
      } else {
        // Remove 'all' from filters
        const index = this.selectedFilters.indexOf("all");
        if (index !== -1) {
          this.selectedFilters = this.selectedFilters.filter(f => f !== "all");
        }

        // Toggle the selected filter
        const valueIndex = this.selectedFilters.indexOf(value);
        if (valueIndex === -1) {
          // Add the filter
          this.selectedFilters.push(value);
        } else {
          // Remove the filter
          this.selectedFilters.splice(valueIndex, 1);
        }

        // If no filters are selected, select 'all'
        if (this.selectedFilters.length === 0) {
          this.selectedFilters = ["all"];
        }
      }
    },
    applyFilters() {
      this.currentPage = 1;
      this.statusFilter = this.selectedFilters.includes('all') ? '' : this.selectedFilters.join(',');
      this.loadSubscribers();
      this.showFilterDropdown = false;
    },
    resetFilters() {
      this.selectedFilters = ["all"];
      this.statusFilter = '';
      this.searchQuery = '';
      this.currentPage = 1;
      this.loadSubscribers();
      this.showFilterDropdown = false;
    },
    handleClickOutside(event) {
      const dropdown = this.$el.querySelector('.filter-dropdown-container');
      if (dropdown && !dropdown.contains(event.target) && this.showFilterDropdown) {
        this.showFilterDropdown = false;
      }
    },
    debouncedSearch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.currentPage = 1;
        this.loadSubscribers();
      }, 500);
    }
  }
};
</script>

<style scoped>
.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 1rem;
}

/* Status badge styles */
.status-badge {
  display: inline-block;
  padding: 8.5px 12px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 400;
}

.status-badge.pending {
  background-color: #fff9e5;
  border: 1px solid #b38600;
  color: #b38600;
}

.status-badge.sent {
  background-color: #e5fff0;
  border: 1px solid #0e8b51;
  color: #0e8b51;
}

.status-badge.failed {
  background-color: #fde9e7;
  border: 1px solid #d51d10;
  color: #d51d10;
}

/* Filter dropdown styles */
.filter-dropdown-container {
  position: relative;
}

/* Pagination styles */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
}

/* Table styles */
.smsir-subscribers-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

.smsir-subscribers-table th, 
.smsir-subscribers-table td {
  padding: 12px 15px;
  text-align: right;
  border-bottom: 1px solid #e6e6e6;
}

.smsir-subscribers-table th {
  background-color: #f9f9f9;
  font-weight: 600;
  color: #333;
}

.smsir-subscribers-table tr:hover {
  background-color: #f5f5f5;
}

/* Add this to close the dropdown when clicking outside */
@media (min-width: 768px) {
  .modal-content {
    width: 600px;
  }
}
</style> 